import { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import RichText from "@/components/RichText";
import { resolveButtonStyle } from "@/lib/ctaButtonStyle";

// Chaque diapositive est un bloc "slide" (theme_service.BLOCK_TYPES["slideshow"])
// réordonnable/ajoutable depuis le panneau de droite comme n'importe quel
// autre bloc (voir BlockList.jsx) — ce composant se contente de les faire
// défiler côté vitrine.
//
// `props.layout` (juin 2026) : "card" (défaut, comportement d'origine
// inchangé — carte arrondie 16:9 centrée) ou "full" (bandeau plein écran
// edge-to-edge, grand titre en majuscules bas-gauche, façon vitrine
// chocolatier). Le mode "full" est purement additif : toute boutique qui ne
// passe pas layout="full" garde exactement le rendu précédent.
export default function SlideshowSection({ props = {}, blocks = [] }) {
  const slides = (blocks || []).filter((b) => b.enabled !== false && b.type === "slide");
  const [index, setIndex] = useState(0);

  if (!slides.length) return null;

  const isFull = props.layout === "full";
  const current = ((index % slides.length) + slides.length) % slides.length;
  const go = (dir) => setIndex((i) => i + dir);

  const Slides = (
    <div
      className={
        isFull
          ? "relative w-full h-[72vh] md:h-[86vh] overflow-hidden bg-ink"
          : "relative rounded-2xl overflow-hidden bg-sand aspect-[16/9]"
      }
    >
      {slides.map((block, i) => {
        const ctaStyle = resolveButtonStyle(block.props, "cta");
        return (
          <div
            key={block.id}
            data-block-key={block.id}
            className="absolute inset-0 transition-opacity duration-500"
            style={{ opacity: i === current ? 1 : 0, pointerEvents: i === current ? "auto" : "none" }}
          >
            {block.props?.image_url && (
              <img src={block.props.image_url} alt="" className="w-full h-full object-cover" />
            )}
            <div
              className={
                isFull
                  ? "absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/25 to-ink/10"
                  : "absolute inset-0 bg-gradient-to-t from-ink/60 via-transparent to-transparent"
              }
            />
            <div
              className={
                isFull
                  ? "absolute bottom-0 left-0 right-0 p-8 md:p-16 max-w-[1240px] mx-auto"
                  : "absolute bottom-0 left-0 right-0 p-6 md:p-10"
              }
            >
              {isFull && block.props?.eyebrow && (
                <p className="text-white/80 text-[11px] md:text-[13px] font-medium uppercase tracking-[0.28em] mb-3 md:mb-5">
                  {block.props.eyebrow}
                </p>
              )}
              {block.props?.heading && (
                <RichText
                  as="h3"
                  className={
                    isFull
                      ? "font-display font-semibold text-white uppercase tracking-tight leading-[1.02] text-4xl md:text-6xl lg:text-7xl max-w-4xl"
                      : "font-display font-semibold text-[24px] md:text-[30px] text-white max-w-lg"
                  }
                  html={block.props.heading}
                />
              )}
              {block.props?.cta_label && (
                <a
                  href={block.props?.cta_url || "#"}
                  className={`inline-flex items-center gap-2 bg-white text-ink px-5 py-2.5 text-[14px] font-semibold hover:opacity-90 transition-opacity ${isFull ? "mt-6 md:mt-8" : "mt-4"} ${ctaStyle.className}`}
                  style={ctaStyle.style}
                  aria-label={!ctaStyle.showText ? block.props.cta_label : undefined}
                >
                  {ctaStyle.showIcon && ctaStyle.Icon && <ctaStyle.Icon className="h-4 w-4" />}
                  {ctaStyle.showText && block.props.cta_label}
                </a>
              )}
            </div>
          </div>
        );
      })}

      {slides.length > 1 && (
        <>
          <button
            type="button"
            aria-label="Diapositive précédente"
            onClick={() => go(-1)}
            className="absolute left-3 md:left-5 top-1/2 -translate-y-1/2 h-9 w-9 md:h-11 md:w-11 rounded-full bg-white/85 hover:bg-white flex items-center justify-center text-ink transition-colors"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            type="button"
            aria-label="Diapositive suivante"
            onClick={() => go(1)}
            className="absolute right-3 md:right-5 top-1/2 -translate-y-1/2 h-9 w-9 md:h-11 md:w-11 rounded-full bg-white/85 hover:bg-white flex items-center justify-center text-ink transition-colors"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5">
            {slides.map((block, i) => (
              <button
                key={block.id}
                type="button"
                aria-label={`Aller à la diapositive ${i + 1}`}
                onClick={() => setIndex(i)}
                className={`h-1.5 rounded-full transition-all ${i === current ? "w-5 bg-white" : "w-1.5 bg-white/60"}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );

  // Mode plein écran : pas de conteneur centré ni de marge — le bandeau
  // occupe toute la largeur du viewport (comportement d'origine conservé
  // pour le mode "card").
  if (isFull) {
    return (
      <section className="w-full">
        {props.heading && (
          <RichText
            as="h2"
            className="font-display font-semibold text-[28px] md:text-[32px] leading-tight tracking-[-0.02em] text-ink max-w-[1240px] mx-auto px-6 md:px-10 pt-section md:pt-section-md"
            html={props.heading}
          />
        )}
        {Slides}
      </section>
    );
  }

  return (
    <section className="max-w-[1240px] mx-auto px-6 md:px-10 py-section md:py-section-md">
      {props.heading && (
        <RichText
          as="h2"
          className="font-display font-semibold text-[28px] md:text-[32px] leading-tight tracking-[-0.02em] text-ink mb-8"
          html={props.heading}
        />
      )}
      {Slides}
    </section>
  );
}
